// The bot check the contact Function asks for: ALTCHA (ADO #1482).
//
// The widget asks this site's own /api/altcha for a challenge, works out the
// answer in the background once the visitor clicks into the form, and hands the
// answer up as a token. functions/_altcha.ts is the other half, and says why
// this is not Cloudflare's Turnstile.
//
// Nothing is compiled in at build time: there is no site key. If the challenge
// route cannot answer (the site has no ALTCHA_HMAC_SECRET), the widget goes to
// its error state and onUnavailable says so, which is what shows the visitor a
// way to reach a person instead (src/components/ReachUs.tsx).
//
// THE LIBRARY LOADS HERE, AND ONLY IN A VISITOR'S BROWSER (#1362). Imported at
// the top of this file it was in every page's bundle, and the stylesheet it adds
// to a page when it loads (about 18,000 characters) was written into every
// prerendered page, and into the template IDX Broker wraps a site's MLS pages
// in. So the widget imports it itself, after the prerender check: a page with no
// form never fetches it, and the build's static HTML never carries it. Until it
// arrives <altcha-widget> is an empty element, which the library upgrades in
// place once it defines it.

import type {} from 'altcha/types/react';
import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    /** Set only by scripts/prerender.mjs, and stripped before the HTML ships. */
    __OMTV_PRERENDER__?: number;
  }
}

type Widget = HTMLElement & {
  reset?: () => void;
  verify?: () => Promise<unknown>;
};

type Props = {
  /** Called with a fresh answer, or with '' when the answer is gone or ran out. */
  onToken: (token: string) => void;
  /** Called if the check cannot be done at all: no challenge, or it failed. */
  onUnavailable: (reason: string) => void;
  /** Change this value to throw the current answer away and start a new one. */
  resetKey?: number;
};

export default function Altcha({ onToken, onUnavailable, resetKey = 0 }: Props) {
  const holder = useRef<Widget>(null);

  // Read inside an effect that must not re-run when the parent re-renders.
  const onTokenRef = useRef(onToken);
  const onUnavailableRef = useRef(onUnavailable);
  onTokenRef.current = onToken;
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    // Headless build-time render: leave the widget alone, so no challenge is
    // fetched and frozen into the static HTML. The real visitor's browser runs it.
    if (window.__OMTV_PRERENDER__) return;
    const widget = holder.current;
    if (!widget) return;
    let gone = false;

    const onState = (event: Event) => {
      const { state, payload } =
        (event as CustomEvent<{ state?: string; payload?: string | null }>).detail ?? {};
      if (state === 'verified' && payload) {
        onTokenRef.current(payload);
        return;
      }
      onTokenRef.current('');
      if (state === 'error') onUnavailableRef.current('the verification could not be completed');
      // It ran out while they were still writing: start a new one without asking them.
      if (state === 'expired') void widget.verify?.();
    };
    widget.addEventListener('statechange', onState);
    // Not fetched at all: offline, blocked, or a deploy replaced the file. The
    // check cannot be done, so say so, and the form shows the way through.
    import('altcha').catch(() => {
      if (!gone) onUnavailableRef.current('the verification could not be loaded');
    });
    return () => {
      gone = true;
      widget.removeEventListener('statechange', onState);
      onTokenRef.current('');
    };
  }, []);

  // The form bumps resetKey after every send, because an answer is good once.
  useEffect(() => {
    if (resetKey) holder.current?.reset?.();
  }, [resetKey]);

  return (
    <div className="mt-6">
      <altcha-widget ref={holder} challenge="/api/altcha" auto="onfocus" name="altcha" />
    </div>
  );
}
